import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, FileText, Loader2, Copy, Check, ChevronRight, Sparkles, Zap, Star, MessageCircle, Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AppShell from '@/components/AppShell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = {
  schema: Globe,
  content: FileText,
  reviews: Star,
  social: MessageCircle,
};

export default function AEOTemplates() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState('');
  const [generated, setGenerated] = useState({});
  const [copied, setCopied] = useState('');

  useEffect(() => {
    axios.get(`${API}/aeo-templates`, { withCredentials: true })
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const generateTemplate = async (templateId) => {
    setGenerating(templateId);
    try {
      const res = await axios.post(`${API}/aeo-templates/generate`, { template_id: templateId }, { withCredentials: true });
      setGenerated(prev => ({ ...prev, [templateId]: res.data.content }));
    } catch (e) {}
    setGenerating('');
  };

  const copyContent = (id) => {
    navigator.clipboard.writeText(generated[id] || '');
    setCopied(id);
    setTimeout(() => setCopied(''), 2000);
  };

  if (loading) {
    return (
      <AppShell active="content">
        <div className="min-h-screen flex items-center justify-center bg-[#F7F4EF]">
          <div className="w-8 h-8 rounded-full animate-spin" style={{ border: '2px solid #D4C9B8', borderTopColor: 'transparent' }} />
        </div>
      </AppShell>
    );
  }

  const templates = data?.templates || [];

  return (
    <AppShell active="content">
      <div className="min-h-screen pb-24 desktop:pb-8 bg-[#F7F4EF]" data-testid="aeo-templates-page">
        <header className="px-6 pt-6 pb-4">
          <div className="flex items-center gap-3 mb-1">
            <button onClick={() => navigate('/content')} className="p-1 rounded-none hover:bg-[#EDE8DF]">
              <ArrowLeft size={20} style={{ color: '#8C7E6E' }} />
            </button>
            <div>
              <h1 className="text-2xl font-medium" style={{ fontFamily: "'DM Sans', sans-serif", color: '#3C3226' }}>
                AEO Content Templates
              </h1>
              <p className="text-[13px]" style={{ color: '#5C5347' }}>AI Engine Optimized content your website needs</p>
            </div>
          </div>
        </header>

        <main className="px-6 max-w-lg mx-auto tablet:max-w-2xl desktop:max-w-5xl">
          <div className="space-y-3">
            {templates.map(t => {
              const Icon = ICONS[t.category] || FileText;
              const isGenerated = !!generated[t.id];
              const isGenerating = generating === t.id;

              return (
                <motion.div
                  key={t.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-sm bg-[#F7F4EF]"
                  style={{ border: '1px solid #D4C9B8' }}
                  data-testid={`aeo-template-${t.id}`}
                >
                  <div className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-none flex items-center justify-center flex-shrink-0" style={{ backgroundColor: t.impact === 'high' ? 'rgba(198,122,92,0.06)' : '#F7F4EF' }}>
                        <Icon size={18} strokeWidth={1.5} style={{ color: t.impact === 'high' ? '#C67A5C' : '#777' }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-[14px] font-medium" style={{ color: '#3C3226' }}>{t.title}</h3>
                          {t.impact === 'high' && (
                            <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded" style={{ backgroundColor: 'rgba(198,122,92,0.08)', color: '#C67A5C' }}>High Impact</span>
                          )}
                        </div>
                        <p className="text-[12px] mb-3" style={{ color: '#5C5347' }}>{t.description}</p>
                        <button
                          onClick={() => generateTemplate(t.id)}
                          disabled={isGenerating}
                          className="flex items-center gap-1.5 px-3.5 py-2 rounded-none text-[12px] font-medium text-[#F0EBE3] transition-opacity hover:opacity-85 disabled:opacity-60"
                          style={{ backgroundColor: '#2C2520' }}
                          data-testid={`generate-${t.id}`}
                        >
                          {isGenerating ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                          {isGenerating ? 'Generating...' : isGenerated ? 'Regenerate' : 'Generate'}
                        </button>
                      </div>
                    </div>
                  </div>

                  {isGenerated && (
                    <div className="px-4 pb-4">
                      <div className="flex gap-2 mb-2">
                        <button onClick={() => copyContent(t.id)} className="flex items-center gap-1 px-3 py-1.5 rounded-none text-[11px] font-medium transition-colors hover:bg-[#EDE8DF]" style={{ border: '1px solid #D4C9B8', color: '#3C3226' }}>
                          {copied === t.id ? <Check size={11} /> : <Copy size={11} />}
                          {copied === t.id ? 'Copied' : 'Copy'}
                        </button>
                      </div>
                      <pre className="p-3 rounded-none text-[11px] leading-relaxed whitespace-pre-wrap overflow-x-auto" style={{ backgroundColor: '#F7F4EF', border: '1px solid #D4C9B8', color: '#5C5347', maxHeight: 300 }}>
                        {generated[t.id]}
                      </pre>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </main>
      </div>
    </AppShell>
  );
}
